/**
 * Performance Tracking Hook
 * 
 * Easy-to-use React hook for tracking component performance
 */

import { useEffect, useRef } from 'react';
import { performanceMonitor } from '../lib/performance';

/**
 * Track component mount and render performance
 * 
 * @example
 * function MyComponent() {
 *   usePerformance('MyComponent');
 *   return <div>Hello</div>;
 * }
 */
export function usePerformance(componentName: string) {
  const mountTimeRef = useRef<number>(0);
  const renderCountRef = useRef<number>(0);

  // Track mount time
  useEffect(() => {
    const startTime = performance.now();
    mountTimeRef.current = startTime;

    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (import.meta.env.DEV) {
        console.log(
          `⚡ ${componentName}: Mounted in ${(endTime - startTime).toFixed(2)}ms, ` +
          `Rendered ${renderCountRef.current} times, ` +
          `Lifetime: ${duration.toFixed(2)}ms`
        );
      }
    };
  }, [componentName]);

  // Track render count
  renderCountRef.current += 1;

  // Log excessive renders
  if (import.meta.env.DEV && renderCountRef.current > 10) {
    console.warn(
      `⚠️ ${componentName} has rendered ${renderCountRef.current} times. ` +
      `Consider memoization.`
    );
  }
}

/**
 * Track async data fetching performance
 * 
 * @example
 * const { isLoading, data, error } = useAsyncPerformance('fetch-user', async () => {
 *   return await fetchUser();
 * });
 */
export function useAsyncPerformance<T>(
  operationName: string,
  operation: () => Promise<T>,
  dependencies: any[] = []
) {
  const startMarkRef = useRef<string>(`${operationName}-${Date.now()}`);

  useEffect(() => {
    performanceMonitor.mark(startMarkRef.current);
    
    operation()
      .then(() => {
        const endMark = `${startMarkRef.current}-end`;
        performanceMonitor.mark(endMark);
        performanceMonitor.measure(operationName, startMarkRef.current, endMark);
      })
      .catch((error) => {
        const endMark = `${startMarkRef.current}-error`;
        performanceMonitor.mark(endMark);
        performanceMonitor.measure(`${operationName} (failed)`, startMarkRef.current, endMark);
        console.error(`❌ ${operationName} failed:`, error);
      });
  }, dependencies);
}

/**
 * Monitor component re-renders and log when excessive
 * 
 * @example
 * function MyComponent() {
 *   useRenderMonitor('MyComponent', 5); // Warn if > 5 renders
 *   return <div>Hello</div>;
 * }
 */
export function useRenderMonitor(componentName: string, threshold: number = 10) {
  const renderCountRef = useRef<number>(0);
  const lastLogRef = useRef<number>(0);

  renderCountRef.current += 1;

  // Log when threshold is exceeded (but only once per 5 seconds to avoid spam)
  if (
    import.meta.env.DEV &&
    renderCountRef.current > threshold &&
    Date.now() - lastLogRef.current > 5000
  ) {
    console.warn(
      `🔄 ${componentName} has rendered ${renderCountRef.current} times. ` +
      `This may indicate a performance issue.`
    );
    lastLogRef.current = Date.now();
  }
}

/**
 * Measure time between two user interactions
 * 
 * @example
 * const measureClick = useInteractionTiming('button-click-to-result');
 * 
 * <button onClick={() => {
 *   measureClick.start();
 *   doSomething();
 *   measureClick.end();
 * }}>
 */
export function useInteractionTiming(interactionName: string) {
  const startMarkRef = useRef<string | null>(null);

  return {
    start: () => {
      startMarkRef.current = `${interactionName}-${Date.now()}`;
      performanceMonitor.mark(startMarkRef.current);
    },
    end: () => {
      if (!startMarkRef.current) {
        console.warn(`No start mark for ${interactionName}`);
        return;
      }

      const endMark = `${startMarkRef.current}-end`;
      performanceMonitor.mark(endMark);
      
      const duration = performanceMonitor.measure(
        interactionName,
        startMarkRef.current,
        endMark
      );

      if (import.meta.env.DEV && duration) {
        const emoji = duration < 100 ? '🟢' : duration < 500 ? '🟡' : '🔴';
        console.log(`${emoji} ${interactionName}: ${duration.toFixed(2)}ms`);
      }

      startMarkRef.current = null;
    },
  };
}

/**
 * Detect and log slow renders (> 16ms)
 * 
 * @example
 * function MyComponent() {
 *   useSlowRenderDetection('MyComponent');
 *   return <div>Hello</div>;
 * }
 */
export function useSlowRenderDetection(componentName: string) {
  const renderStartRef = useRef<number>(0);

  // Before render
  renderStartRef.current = performance.now();

  // After render (use useEffect)
  useEffect(() => {
    const renderDuration = performance.now() - renderStartRef.current;
    
    // 16ms = 60fps threshold
    if (import.meta.env.DEV && renderDuration > 16) {
      console.warn(
        `🐌 ${componentName} rendered slowly: ${renderDuration.toFixed(2)}ms ` +
        `(Target: < 16ms for 60fps)`
      );
    }
  });
}
